import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import StarIcon from '@mui/icons-material/Star'
import { carregarArvore, ehFavorito, useNovoPortal } from './estado'
import { marcaDoAmbiente } from './tema'
import './dashboard/dashboard.scss'

interface Resultado {
  dashboardId: number
  ambienteId: number
  nome: string
  ambiente: string
  grupo: string
  marca: string
  favorito: boolean
}

const MAXIMO = 40

function normalizar(t: string) {
  return t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

/** Busca de dashboards em todos os ambientes da pessoa. Abre com Ctrl K (ou ⌘K) ou pelo botão do topo. */
export default function BuscaGlobal({ aberta, aoFechar }: { aberta: boolean; aoFechar: () => void }) {
  const est = useNovoPortal()
  const navigate = useNavigate()
  const [texto, setTexto] = useState('')
  const [ativo, setAtivo] = useState(0)
  const campo = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!aberta) return
    carregarArvore()
    setTexto('')
    setAtivo(0)
    setTimeout(() => campo.current?.focus(), 0)
  }, [aberta])

  const todos = useMemo<Resultado[]>(() => {
    const lista: Resultado[] = []
    ;(est.arvore || []).forEach(a => a.grupos.forEach(g => g.dashboards.forEach(d => lista.push({
      dashboardId: d.id, ambienteId: a.id, nome: d.nome, ambiente: a.nome, grupo: g.nome,
      marca: marcaDoAmbiente(est.marcas, est.ambientes, a.id).nome,
      favorito: ehFavorito(est, d.id, a.id)
    }))))
    return lista
  }, [est])

  const resultados = useMemo(() => {
    const q = normalizar(texto.trim())
    const palavras = q.split(/\s+/).filter(Boolean)
    const achados = palavras.length
      ? todos.filter(r => { const alvo = normalizar(`${r.nome} ${r.ambiente} ${r.grupo} ${r.marca}`); return palavras.every(p => alvo.includes(p)) })
      : todos.filter(r => r.favorito)
    // Favoritos e nomes que começam com o que foi digitado vêm primeiro.
    return achados.sort((x, y) =>
      Number(y.favorito) - Number(x.favorito) ||
      Number(normalizar(y.nome).startsWith(q)) - Number(normalizar(x.nome).startsWith(q)) ||
      x.nome.localeCompare(y.nome)).slice(0, MAXIMO)
  }, [todos, texto])

  useEffect(() => { setAtivo(0) }, [texto])

  if (!aberta) return null

  const abrir = (r: Resultado) => {
    aoFechar()
    navigate(`/app/dashboard/${r.dashboardId}/${r.ambienteId}`)
  }

  const teclas = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); aoFechar() }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setAtivo(i => Math.min(i + 1, resultados.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAtivo(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' && resultados[ativo]) { e.preventDefault(); abrir(resultados[ativo]) }
  }

  return (
    <div className="np-busca-veu" onMouseDown={e => { if (e.target === e.currentTarget) aoFechar() }}>
      <div className="np-busca" role="dialog" aria-modal="true" aria-label="Busca de dashboards">
        <label className="np-busca-campo">
          <SearchIcon />
          <input ref={campo} value={texto} onChange={e => setTexto(e.target.value)} onKeyDown={teclas}
                 placeholder="Buscar dashboard em todos os seus ambientes" aria-label="Buscar dashboard"
                 role="combobox" aria-expanded="true" aria-controls="np-busca-lista" aria-activedescendant={resultados[ativo] ? `np-busca-${ativo}` : undefined} />
          <kbd>Esc</kbd>
        </label>
        <div className="np-busca-lista" id="np-busca-lista" role="listbox">
          {!est.arvore && !est.erroArvore && <p className="np-busca-vazio">Carregando seus dashboards…</p>}
          {est.erroArvore && <p className="np-busca-vazio">{est.erroArvore}</p>}
          {est.arvore && !texto.trim() && resultados.length > 0 && <div className="np-busca-sec">Favoritos</div>}
          {est.arvore && !texto.trim() && resultados.length === 0 && <p className="np-busca-vazio">Digite o nome do dashboard, do ambiente ou do grupo.</p>}
          {est.arvore && texto.trim() && resultados.length === 0 && <p className="np-busca-vazio">Nenhum dashboard com “{texto.trim()}”.</p>}
          {resultados.map((r, i) => (
            <button key={`${r.ambienteId}-${r.dashboardId}`} id={`np-busca-${i}`} type="button" role="option" aria-selected={i === ativo}
                    className="np-busca-item" onMouseEnter={() => setAtivo(i)} onClick={() => abrir(r)}>
              <span className="np-busca-nome"><b>{r.nome}</b><small>{r.ambiente} · {r.grupo}</small></span>
              {r.favorito && <StarIcon className="np-busca-estrela" fontSize="small" />}
              <span className="np-busca-marca">{r.marca}</span>
            </button>
          ))}
        </div>
        <div className="np-busca-rodape"><span><kbd>↑</kbd><kbd>↓</kbd> navegar</span><span><kbd>Enter</kbd> abrir</span></div>
      </div>
    </div>
  )
}
